import { TranslationKey } from './../../../types/translation/TranslationKey';
import React, { useContext, useMemo, useState } from 'react';
import { translate } from '../../../types/translation/Translator';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Task, TaskStatus, TaskType } from '../../../types/task';
import { StockLocationContext } from '../../../context/StockLocationContext';
import { TaskContext } from '../../../context/TaskContext';
import CreateTaskModal from './Modals/CreateTaskModal';
import TaskIcon from '@mui/icons-material/Task';
import DeleteTaskModal from './Modals/DeleteTaskModal';
import { CompanyContext } from '../../../context/CompanyContext';
import { CompanyRoleAssignmentContext } from '../../../context/CompanyRoleAssignmentContext';
import { CompanyRole } from '../../../types/company_role_assignment/companyRoleAssignment';
import TaskStatusLabel from '../Common/TaskStatusLabel';
import { StockLocationRoleAssignmentContext } from '../../../context/StockLocationRoleAssignmentContext';
import { StockLocationRole } from '../../../types/stock_location_role_assignment/stockLocationRoleAssignment';
import SearchBarWithFilter from '../../../VentoryUI/components/common/SearchBarWithFilter';
import Table from '../../components/Table';
import { TaskFilter } from '../../../VentoryUI/components/filters/TaskFilter';
import { UserContext } from '../../../context/UserContext';
import { BinStatusContext } from '../../../context/BinStatusContext';
import { ProductMasterDataContext } from '../../../context/ProductMasterDataContext';
import { TagRelationContext } from '../../../context/TagRelationContext';
import { Routing } from '../../../util/routes.util';
import { CustomFieldContext } from '../../../context/CustomFieldContext';
import { BinContext } from '../../../context/BinContext';
import { DeleteIconButton, SettingsIconButton } from '../../icons';
import { CreateButton } from '../../components/Button';
import { TaskExportModal } from './Settings/Panes/Export/TaskExportModal';

export default function TaskOverviewPane() {
  const navigate = useNavigate();

  const [params] = useSearchParams();
  const filterParam = params.get('filter') || '';

  const { tasks, tasksLoading } = useContext(TaskContext);
  const { stockLocations } = useContext(StockLocationContext);
  const { companyUsers, locale } = useContext(UserContext);
  const { currentCompany, companies } = useContext(CompanyContext);
  const { hasCompanyRole } = useContext(CompanyRoleAssignmentContext);
  const { hasStockLocationRole } = useContext(StockLocationRoleAssignmentContext);
  const { binStatuses } = useContext(BinStatusContext);
  const { productMasterData } = useContext(ProductMasterDataContext);
  const { tagRelations } = useContext(TagRelationContext);
  const { customFields } = useContext(CustomFieldContext);
  const { bins } = useContext(BinContext);

  const [openCreateModal, setOpenCreateModal] = useState<boolean>(false);
  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
  const [exportModalOpen, setExportModalOpen] = useState<boolean>(false);
  const [exportId, setExportId] = useState<string | undefined>();
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const [items, setItems] = useState<Task[]>([...tasks.values()]);

  const isPartner = currentCompany.partner;

  const headers = [
    {
      key: 'company',
      name: translate({ key: TranslationKey.company }),
      text: (item: Task) => companies.get(item.companyId)?.name || '',
    },
    {
      key: 'number',
      name: translate({ key: TranslationKey.number }),
      text: (item: Task) => {
        return item.number;
      },
    },
    {
      key: 'type',
      name: translate({ key: TranslationKey.type }),
      text: (item: Task) => {
        if (item.type === TaskType.cycle) {
          return translate({ key: TranslationKey.cycleCount });
        } else if (item.type === TaskType.blind) {
          return translate({ key: TranslationKey.blindCount });
        } else {
          return translate({ key: TranslationKey.recount });
        }
      },
    },
    {
      key: 'stockLocation',
      name: translate({ key: TranslationKey.stockLocation }),
      text: (item: Task) => stockLocations.get(item.stockLocationId)?.name || 'Unknown Stock Location',
    },
    {
      key: 'createdOn',
      name: translate({ key: TranslationKey.createdAt }),
      text: (item: Task) => (item.createdAt ? new Date(item.createdAt).toLocaleDateString(locale) : ''),
      sortValue: (item: Task) => item.createdAt,
    },
    {
      key: 'dueDate',
      name: translate({ key: TranslationKey.dueDate }),
      text: (item: Task) => {
        return item.dueDate ? new Date(item.dueDate).toLocaleDateString(locale) : '';
      },
      sortValue: (item: Task) => item.dueDate,
    },
    {
      key: 'completedOn',
      name: translate({ key: TranslationKey.completedOn }),
      text: (item: Task) => (item.completedOn ? new Date(item.completedOn).toLocaleDateString(locale) : ''),
      sortValue: (item: Task) => item.dueDate,
    },
    {
      key: 'report',
      name: '',
      text: (item: Task) =>
        item.status === TaskStatus.complete || item.status === TaskStatus.processed ? (
          <div
            className='h-7'
            onClick={event => {
              event.preventDefault();
              event.stopPropagation();
              setExportId(item.id);
              setExportModalOpen(true);
            }}
          >
            <TaskIcon />
          </div>
        ) : (
          ''
        ),
    },
    {
      key: 'status',
      name: translate({ key: TranslationKey.status }),
      text: (item: Task) => <TaskStatusLabel task={item} />,
      sortValue: (item: Task) => item.status,
    },
  ].filter(h => h.key !== 'company' || isPartner);

  const isAdministrator = hasCompanyRole(currentCompany.id, CompanyRole.administrator);
  const isSupervisor = !![...stockLocations.values()].filter(sl =>
    hasStockLocationRole(currentCompany.id, sl.id, StockLocationRole.STOCK_LOCATION_SUPERVISOR),
  ).length;

  const allItems = useMemo(() => {
    return [...tasks.values()];
  }, [tasks]);

  const filter = new TaskFilter(
    stockLocations,
    companyUsers,
    binStatuses,
    productMasterData,
    tagRelations,
    customFields,
    bins,
    currentCompany,
  );

  return (
    <>
      <CreateTaskModal open={openCreateModal} setOpen={setOpenCreateModal} />
      <DeleteTaskModal
        open={openDeleteModal}
        setOpen={v => {
          setOpenDeleteModal(v);
          setSelected(new Set());
        }}
        taskIds={selected}
      />
      {exportModalOpen && exportId ? (
        <TaskExportModal
          open={exportModalOpen}
          setOpen={open => {
            if (!open) setExportId(undefined);
            setExportModalOpen(open);
          }}
          id={exportId}
        />
      ) : null}

      <div className='flex flex-grow flex-col gap-4 px-8 py-6'>
        <div className='flex justify-between gap-2'>
          <SearchBarWithFilter
            loading={tasksLoading}
            items={allItems}
            setItems={setItems}
            placeholder={translate({ key: TranslationKey.filterTasks, count: 'plural' })}
            filter={filter}
          />
          <div className='flex gap-1'>
            {selected.size && isAdministrator ? <DeleteIconButton onClick={() => setOpenDeleteModal(true)} /> : null}
            <CreateButton onClick={() => setOpenCreateModal(true)} />
            {isAdministrator ? (
              <SettingsIconButton onClick={() => navigate(Routing.tasks.settings.overview())} />
            ) : null}
          </div>
        </div>
        <Table
          title={translate({ key: TranslationKey.task, count: 'plural' })}
          loading={tasksLoading}
          items={items}
          headers={headers}
          onClick={item => navigate(Routing.tasks.detail(item.id))}
          selectedValues={selected}
          onSelected={items => setSelected(new Set(items))}
          totalItemCount={allItems.length}
          filter={filter}
        />
      </div>
    </>
  );
}
